import { useEffect, useState } from "react";
import {
    FaUserCircle,
    FaKey,
    FaSpinner,
    FaSave
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { clearAuthStorage } from "../utils/auth";

import "./ManageAdmins.css";

const API_URL = process.env.REACT_APP_API_URL;

const ROLE_LABELS = { superadmin: "SuperAdmin", admin: "Admin", user: "User" };

function ManageProfile() {

    const [profile, setProfile] = useState(null);

    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile_no: "" });

    const [isLoading, setIsLoading] = useState(true);

    const [isSavingProfile, setIsSavingProfile] = useState(false);

    const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });

    const [isChangingPassword, setIsChangingPassword] = useState(false);

    useEffect(() => {

        loadProfile();

    }, []);

    const loadProfile = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/profile`);

            const data = await res.json();

            if (data.status) {

                setProfile(data.user);

                setForm({

                    first_name: data.user.first_name || "",

                    last_name: data.user.last_name || "",

                    email: data.user.email || "",

                    mobile_no: data.user.mobile_no || ""

                });

            } else {

                toast.error(data.message || "Failed to load your profile.");

            }

        } catch (err) {

            console.error("Failed to load profile:", err);

            toast.error("Unable to load your profile. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    const handleFormChange = (e) => {

        setForm({ ...form, [e.target.name]: e.target.value });

    };

    const handlePasswordFormChange = (e) => {

        setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });

    };

    const handleSaveProfile = async (e) => {

        e.preventDefault();

        setIsSavingProfile(true);

        try {

            const res = await fetch(`${API_URL}/portal/profile`, {

                method: "PUT",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify(form)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message);

                setProfile(data.user);

                // Keep the name shown elsewhere (e.g. if a header ever
                // shows it) in sync with what's actually saved.
                const stored = JSON.parse(localStorage.getItem("user") || "{}");
                localStorage.setItem("user", JSON.stringify({ ...stored, ...data.user }));

            } else {

                toast.error(data.message || "Failed to update your profile.");

            }

        } catch (err) {

            console.error("Failed to update profile:", err);

            toast.error("Something went wrong while saving.");

        } finally {

            setIsSavingProfile(false);

        }

    };

    const handleChangePassword = async (e) => {

        e.preventDefault();

        if (!passwordForm.current_password || !passwordForm.new_password) {

            toast.warn("Enter your current password and a new password.");
            return;

        }

        if (passwordForm.new_password !== passwordForm.confirm_password) {

            toast.warn("New password and confirmation don't match.");
            return;

        }

        setIsChangingPassword(true);

        try {

            const res = await fetch(`${API_URL}/portal/change-password`, {

                method: "POST",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({

                    current_password: passwordForm.current_password,

                    new_password: passwordForm.new_password

                })

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success(data.message);

                // The old JWT is still technically valid until it
                // expires (this app doesn't keep a server-side
                // revocation list), but forcing a fresh login here at
                // least makes sure THIS session immediately starts
                // using the new password, and gives clear
                // confirmation it actually took effect.
                setTimeout(() => {

                    // Only clears auth-related keys - preserves
                    // device preferences like Theme Settings' saved
                    // color across this forced re-login.
                    clearAuthStorage();

                    window.location.href = "/";

                }, 1500);

            } else {

                toast.error(data.message || "Failed to change your password.");

            }

        } catch (err) {

            console.error("Failed to change password:", err);

            toast.error("Something went wrong while changing your password.");

        } finally {

            setIsChangingPassword(false);

        }

    };

    if (isLoading) {

        return (

            <div className="page">
                <p className="empty-note"><FaSpinner className="spin" /> Loading your profile...</p>
            </div>

        );

    }

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2><FaUserCircle /> My Profile</h2>
                    <p className="page-subtitle">
                        View and update your own account. Available to every role - what you see here
                        is always just your own account, nothing else.
                    </p>
                </div>

            </div>

            {profile && (

                <div className="inline-form-container">

                    <div className="inline-form-header">
                        <h3>Account Details</h3>
                    </div>

                    <div className="inline-form-grid">

                        <div>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                                Username (not editable)
                            </label>
                            <input value={profile.username} disabled />
                        </div>

                        <div>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                                Role
                            </label>
                            <input value={ROLE_LABELS[profile.role] || profile.role} disabled />
                        </div>

                    </div>

                    <form onSubmit={handleSaveProfile}>

                        <div className="inline-form-grid">

                            <input
                                name="first_name"
                                placeholder="First Name"
                                value={form.first_name}
                                onChange={handleFormChange}
                                disabled={isSavingProfile}
                            />

                            <input
                                name="last_name"
                                placeholder="Last Name"
                                value={form.last_name}
                                onChange={handleFormChange}
                                disabled={isSavingProfile}
                            />

                            <input
                                name="email"
                                type="email"
                                placeholder="Email (optional)"
                                value={form.email}
                                onChange={handleFormChange}
                                disabled={isSavingProfile}
                            />

                            <input
                                name="mobile_no"
                                placeholder="Mobile Number (optional)"
                                value={form.mobile_no}
                                onChange={handleFormChange}
                                disabled={isSavingProfile}
                            />

                        </div>

                        {profile.mobile_no && (

                            <p style={{ fontSize: "12px", color: profile.mobile_verified ? "#15803d" : "#92400e", margin: "0 0 10px" }}>
                                Mobile {profile.mobile_verified ? "verified" : "not verified"}
                                {!profile.mobile_verified && " - use \"Verify Mobile Number\" from the login page if you received an OTP."}
                            </p>

                        )}

                        <div className="inline-form-actions">

                            <button type="submit" className="save-btn" disabled={isSavingProfile}>
                                {isSavingProfile ? <><FaSpinner className="spin" /> Saving...</> : <><FaSave /> Save Profile</>}
                            </button>

                        </div>

                    </form>

                </div>

            )}

            <div className="inline-form-container" style={{ marginTop: "20px" }}>

                <div className="inline-form-header">
                    <h3><FaKey /> Change Password</h3>
                </div>

                <form onSubmit={handleChangePassword}>

                    <div className="inline-form-grid">

                        <input
                            name="current_password"
                            type="password"
                            placeholder="Current Password"
                            value={passwordForm.current_password}
                            onChange={handlePasswordFormChange}
                            disabled={isChangingPassword}
                            className="full-width"
                        />

                        <input
                            name="new_password"
                            type="password"
                            placeholder="New Password (at least 6 characters)"
                            value={passwordForm.new_password}
                            onChange={handlePasswordFormChange}
                            disabled={isChangingPassword}
                        />

                        <input
                            name="confirm_password"
                            type="password"
                            placeholder="Confirm New Password"
                            value={passwordForm.confirm_password}
                            onChange={handlePasswordFormChange}
                            disabled={isChangingPassword}
                        />

                    </div>

                    <div className="inline-form-actions">

                        <button type="submit" className="save-btn" disabled={isChangingPassword}>
                            {isChangingPassword ? <><FaSpinner className="spin" /> Changing...</> : <><FaKey /> Change Password</>}
                        </button>

                    </div>

                </form>

            </div>

        </div>

    );

}

export default ManageProfile;
